<script setup lang="ts">
import { computed } from 'vue'

/**
 * Shared native date input.
 *
 * iOS WebKit (Safari and Chrome on iPhone) renders nothing inside an empty
 * date input: there is no native "dd/mm/yyyy" placeholder as on desktop.
 * This wrapper adds a visible hint while the field is empty so the control
 * never looks blank or broken. The hint is decorative only; the label and
 * any error copy remain the accessible name and description.
 *
 * All other attributes (aria-invalid, aria-describedby, min, max, name,
 * enterkeyhint) fall through to the input element.
 */
defineOptions({ inheritAttrs: false })

const model = defineModel<string>({ required: true })

defineProps<{
  id: string
  /** Hint shown while empty. Defaults to the shared "Select a date" copy. */
  hint?: string
}>()

const isEmpty = computed(() => !model.value)
</script>

<template>
  <span class="sa-date-field" :class="{ 'sa-date-field--empty': isEmpty }">
    <input
      :id="id"
      v-model="model"
      class="sa-input"
      type="date"
      v-bind="$attrs"
    />
    <!-- Only painted where the platform gives no native placeholder (iOS). -->
    <span class="sa-date-field__hint" aria-hidden="true">
      {{ hint || $t('Dashboard.Giving.AddDonationModal.DateHint') }}
    </span>
  </span>
</template>
